import React, { useCallback, useEffect, useMemo, useState } from "react";
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import StockScreenerDashboard from "./pages/StockScreenerDashboard";

export default function App() {
  const [initialized, setInitialized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [callbackStatus, setCallbackStatus] = useState("processing");
  const [loginNotice, setLoginNotice] = useState("");
  const [authUser, setAuthUser] = useState(null);

  const callbackTarget = useMemo(() => {
    const path = window.location.pathname;
    const query = window.location.search;
    const params = new URLSearchParams(query);
    const isAdminCallback = /^\/admin(\/[^/]+)?\/callback$/.test(path);
    const isClientCallback = /^\/client\/[^/]+\/callback$/.test(path);
    const hasToken = params.has("request_token") || params.has("code") || params.has("tokenId");

    if (!hasToken || (!isAdminCallback && !isClientCallback)) {
      return null;
    }

    return `/api${path}${query}`;
  }, []);

  useEffect(() => {
    if (!callbackTarget) return;

    const completeLogin = async () => {
      try {
        const res = await fetch(callbackTarget, { cache: "no-store" });

        if (!res.ok) {
          throw new Error(`Callback failed with ${res.status}`);
        }

        if (window.opener) {
          window.opener.postMessage("LOGIN_SUCCESS", window.location.origin);
          window.close();
        } else {
          setCallbackStatus("complete");
          window.location.replace("/");
        }
      } catch (err) {
        console.error(err);
        setCallbackStatus("failed");
      }
    };

    completeLogin();
  }, [callbackTarget]);

  useEffect(() => {
    if (callbackTarget) return;

    const checkAdminAuth = async () => {
      try {
        const res = await fetch("/api/admin/auth/me", { cache: "no-store" });
        const contentType = String(res.headers.get("content-type") || "").toLowerCase();
        if (!contentType.includes("application/json")) {
          throw new Error(`Admin session API returned ${contentType || "a non-JSON response"} (HTTP ${res.status})`);
        }
        const data = await res.json();

        if (res.ok && data.authenticated) {
          setAuthUser(data);
          setInitialized(true);
        }
      } catch (err) {
        console.error("Admin auth check failed:", err);
      } finally {
        setAuthChecked(true);
      }
    };

    checkAdminAuth();
  }, [callbackTarget]);

  const handleInitSuccess = (loginData) => {
    setLoginNotice("");
    setAuthUser(loginData || null);
    setInitialized(true);
  };

  const handleSessionExpired = useCallback((message) => {
    setLoginNotice(message || "Your session expired. Please log in again.");
    setAuthUser(null);
    setInitialized(false);
  }, []);

  const handleLogout = async () => {
    try {
      const response = await fetch("/api/admin/auth/logout", {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store"
      });

      if (!response.ok) {
        throw new Error(`Admin logout failed with ${response.status}`);
      }

      setInitialized(false);
      setAuthUser(null);
    } catch (err) {
      console.error("Admin logout failed:", err);
      window.alert("Logout failed. Your SuperAdmin session is still active.");
    }
  };

  if (callbackTarget) {
    return (
      <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ border: "1px solid #ccc", padding: 30 }}>
          <h2>MintXPro Login</h2>
          <p>{callbackStatus === "failed" ? "Login callback failed." : "Completing login..."}</p>
        </div>
      </div>
    );
  }

  if (!authChecked) {
    return (
      <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div>Checking admin session...</div>
      </div>
    );
  }

  return (
    <>
      {!initialized ? (
        <Login onSuccess={handleInitSuccess} notice={loginNotice} />
      ) : (
        authUser?.role === "stock_screener"
          ? <StockScreenerDashboard username={authUser.username} onLogout={handleLogout} />
          : <AdminDashboard onLogout={handleLogout} onSessionExpired={handleSessionExpired} />
      )}
    </>
  );
}
